import { CheckCircle2, Circle } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/features/auth/useAuth'
import { formatDateTime } from '@/lib/utils'

/** Landing page: proves the login works, shows what is done and what is still open. */
export function DashboardPage() {
  const { user, profile } = useAuth()

  /** Последние изменения, свежие сверху (подробности — web/SESSION_HANDOFF.md, решения 29–39). */
  const recent = [
    '15.10 — тип ответа «Факт»: не вопрос, а скрытые от пользователя эффекты (например постоянная работа сантехника у группы); в приложении цена 0 в смете = пункт бесплатный',
    '12.10 — эффект «с пунктом затрат»: вместе с выбранным материалом автоматически добавляется постоянный пункт (например работа по укладке)',
    '12.10 — выбор пункта затрат в эффектах с поиском по названию, переводам, коду и категории',
    '12.10 — категория в вопросах «Выбор» и полях повторителя выбирается списком с группами и поиском',
    '09.10 — группы категорий: поле «Группа», фильтр, заголовки групп в списках',
    '09.10 — категории без кода, порядок присваивается автоматически; выравнивание — в «Несоответствиях»',
    '09.10 — кнопка «Вставить перевод» в полях en / az',
    '08.10 — категории привязаны по id: переименование больше не отвязывает пункты и вопросы',
    '08.10 — «Симулировать калькуляцию» у типа раздела: ответы на вопросы помещения и живой расчёт, ничего не сохраняется',
    '08.10 — вопросы раздела разделены на «от групп» (просмотр) и «индивидуальные»',
    '07.10 — раздел «Несоответствия»: проверки каталога и правил, переходы к записям, CSV, исправление в один клик, бейдж в меню',
    '07.10 — палитра ключей фактов в диалоге подсказки группы',
  ]

  /** Планы на будущее: запланировано или обсуждалось, но не сделано. */
  const backlog = [
    'Выкат: пересобрать приложение → задеплоить консоль → в «Несоответствиях» привязать категории по id и упорядочить их',
    'Сквозная проверка: вопрос из консоли → интервью в приложении → строка сметы; сверка симуляции с приложением',
    'Настоящая иерархия «группа → категория» с наследованием атрибутов (пока группы только для отображения)',
    'Симуляция сразу нескольких помещений и сохранение набора тестовых ответов',
    'Проверка выражений прямо в редакторах и удаление записей-сирот из «Несоответствий»',
    'Перестановка категорий вручную и перетаскивание для порядка узлов и вариантов',
    'Запрет пустых и повторяющихся ключей полей повторителя в вебе (как в приложении)',
    'Дублирование вопроса или раздела',
    'Firebase App Check',
  ]

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold">Обзор</h1>
        <p className="text-muted-foreground text-sm">
          Веб-консоль администратора Estimate Pro. Данные читаются напрямую из Cloud Firestore.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Вы вошли</CardTitle>
          <CardDescription>Профиль из коллекции users</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
          <Field label="Email" value={profile?.email ?? user?.email ?? '—'} />
          <Field label="Имя" value={profile?.displayName ?? user?.displayName ?? '—'} />
          <Field label="Роль" value={profile?.role ?? '—'} />
          <Field label="Тариф" value={profile?.plan ?? '—'} />
          <Field label="UID" value={user?.uid ?? '—'} />
          <Field label="Профиль создан" value={formatDateTime(profile?.createdAt)} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Сделано недавно</CardTitle>
          <CardDescription>
            Изменения 07–12.10.2026. Полная история — в web/SESSION_HANDOFF.md и
            web/DEVELOPMENT.md
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          {recent.map((label) => (
            <div key={label} className="flex items-center gap-2">
              <CheckCircle2 className="size-4 shrink-0 text-emerald-500" />
              <span>{label}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Планы</CardTitle>
          <CardDescription>Запланировано или обсуждалось, но пока не сделано</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          {backlog.map((label) => (
            <div key={label} className="flex items-center gap-2">
              <Circle className="text-muted-foreground size-4 shrink-0" />
              <span className="text-muted-foreground">{label}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className="truncate font-medium">{value}</div>
    </div>
  )
}
